/**
 * This is an algorithm to find the key of a song.
 *
 * Implementation of the Krumhansl-Schmuckler key-finding algorithm:
 *  http://rnhart.net/articles/key-finding/
 *
 * This could live in SongStore, but keeping it here to try to keep songstore clean.
 */

import { KeyAndScale, Scale, StemType } from '@arpeggi/shared'
import { IMelodicLoopStore } from '../stores/song/loop/MelodicLoopStore'
import { ISongStore } from '../stores/song/SongStore'
import { IAudioStemStore } from '../stores/song/stem/AudioStemStore'
import { IInstrumentStemStore } from '../stores/song/stem/InstrumentStemStore'

/** Returns the Key of the given SongStore. */
export const findKey = (song: ISongStore): KeyAndScale => {
  // Simple solution for now: if a stem has ANY midi instrument stems, use those to calculate key.
  // Otherwise (if only drums + audio), use the most frequent clip key
  if (
    song.stems.find((stem) => {
      stem.stemType === StemType.INSTRUMENT
    })
  ) {
    return findKeyForDurations(getNoteDurations(song))
  } else {
    return findKeyForAudioStems(song)
  }
}

/* In the key-finding algorithm one value of the coordinates represents a profile of a major key or a minor key. */
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88]
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17]

/**
 * The other value of the coordinates is the total durations of each pitch class in the piece of music being analyzed.
 * This returns the durations array from the given song, by simply incrementing each time a note appears.
 *
 * @returns an array with the count of each pitch, index 0 = C, index 1 = C#, etc.
 */
/** Exported for testing only. */
export const getNoteDurations = (song: ISongStore): number[] => {
  let sum = Array(12).fill(0)
  song.stems.forEach((stem) => {
    if (stem.stemType === StemType.INSTRUMENT) {
      const stemCount = getStemNoteCount(stem as IInstrumentStemStore)
      sum = sum.map((value, index) => value + stemCount[index])
    }
  })
  return sum
}

/** Gets the note count for the given stem. */
/** Exported for testing only. */
export const getStemNoteCount = (stem: IInstrumentStemStore): number[] => {
  let sum = Array(12).fill(0)
  stem.stemSettings.arrangement.forEach((loopIndex) => {
    if (loopIndex === 0) return
    const loopCount = getLoopNoteCount(stem.loops[loopIndex - 1].melodicLoop)
    sum = sum.map((value, index) => value + loopCount[index])
  })
  return sum
}

/** Gets the note count for the given loop. */
/** Exported for testing only. */
export const getLoopNoteCount = (loop: IMelodicLoopStore): number[] => {
  let sum = Array(12).fill(0)
  loop.notes.notes.forEach((note) => {
    const noteIndex = note.note % 12
    sum[noteIndex]++
  })
  return sum
}

/** Returns the key of the given durations. Note: Key might be "None" if this is a drums-only song. */
export const findKeyForDurations = (durations: number[]): KeyAndScale => {
  let maxKey
  let maxKeyMajor
  let maxKeyCoefficient = -10000

  for (let currentKey = 0; currentKey < 24; currentKey++) {
    const major = currentKey <= 11
    const keyToUse = major ? currentKey : currentKey - 12
    const currentCoefficient = getCorrelationCoefficient(durations, keyToUse, major)
    if (currentCoefficient > maxKeyCoefficient) {
      maxKey = keyToUse
      maxKeyMajor = major ? Scale.MAJOR : Scale.MINOR
      maxKeyCoefficient = currentCoefficient
    }
  }

  const key = new KeyAndScale()
  key.key = maxKey
  key.scale = maxKeyMajor
  return key
}

/** Gets the correlation coefficient of the durations for the given key. */
/** Exported for testing only. */
export const getCorrelationCoefficient = (durations: number[], key: number, major: boolean) => {
  const yValues = [...durations]
  const xValues = major ? MAJOR_PROFILE : MINOR_PROFILE
  if (yValues.length !== 12 || yValues.length !== xValues.length) {
    throw 'Invalid durations array, must be length 12: ' + JSON.stringify(yValues)
  }

  // "Rotate" the durations array by key to line it up correctly.
  rotate(yValues, key)

  const xMean = mean(xValues)
  const yMean = mean(yValues)

  let numerator = 0
  let denominatorX = 0
  let denominatorY = 0
  for (let i = 0; i < 12; i++) {
    numerator += (xValues[i] - xMean) * (yValues[i] - yMean)
    denominatorX += (xValues[i] - xMean) * (xValues[i] - xMean)
    denominatorY += (yValues[i] - yMean) * (yValues[i] - yMean)
  }

  return numerator / Math.sqrt(denominatorX * denominatorY)
}

const mean = (array) => array.reduce((a, b) => a + b) / array.length

const rotate = (array, count) => {
  count -= array.length * Math.floor(count / array.length)
  array.push.apply(array, array.splice(0, count))
}

/**
 * Naive algorithm to just return the most frequently used KeyAndScale across all clips.
 * Defaults to NONE
 */
const findKeyForAudioStems = (song: ISongStore) => {
  // 12 * 3 + 1, total number of types of Keys
  let sum = Array(37).fill(0)
  song.stems.forEach((stem) => {
    if (stem.stemType === StemType.AUDIO) {
      const audioStem = stem as IAudioStemStore
      audioStem.clips.forEach((clip) => {
        const arpMedia = clip.sampleWithEffects.getArpMedia()
        if (arpMedia) {
          sum[arpMedia.metadata.key]++
        }
      })
    }
  })
  let mostFrequentKey = 0
  let mostFrequentValue = 0
  sum.forEach((value, key) => {
    // Don't count "NONE" clips here
    if (value > mostFrequentValue && key !== 0) {
      mostFrequentKey = key
      mostFrequentValue = value
    }
  })

  return KeyAndScale.from(mostFrequentKey)
}
